import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Spec 03 · Elevation — "flat: hairline only · cards, fields".
 * Radius 3, 24px padding. Cards never take a shadow; only drawers,
 * popovers and modals do.
 */

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-md border border-hairline bg-card text-card-foreground', className)}
      {...props}
    />
  )
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 p-6 pb-0', className)} {...props} />
  )
)
CardHeader.displayName = 'CardHeader'

/** Card titles are the h3 role: 16/700, tight. */
const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-[16px] font-bold tracking-[-0.01em]', className)} {...props} />
  )
)
CardTitle.displayName = 'CardTitle'

/** Eyebrow above a card title — 11/0.2em uppercase in ink-muted. */
const CardLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('text-[13px] font-medium leading-none text-ink-muted', className)}
      {...props}
    />
  )
)
CardLabel.displayName = 'CardLabel'

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-[12px] leading-[1.55] text-ink-muted', className)} {...props} />
  )
)
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('p-6', className)} {...props} />
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-3 p-6 pt-0', className)} {...props} />
  )
)
CardFooter.displayName = 'CardFooter'

/**
 * Spec 11 · Admin stat tile — label, figure in Chakra Petch, delta.
 * `flagged` is the one tile that needs attention: accent border on the
 * tint, with the label and delta in accent-deep.
 */
interface StatTileProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode
  value: React.ReactNode
  delta?: React.ReactNode
  tone?: 'neutral' | 'up' | 'down'
  flagged?: boolean
}

function StatTile({ label, value, delta, tone = 'neutral', flagged, className, ...props }: StatTileProps) {
  return (
    <div
      className={cn(
        'rounded-md border p-4',
        flagged ? 'border-signal bg-signal-tint' : 'border-hairline',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'text-[12px] font-medium leading-none',
          flagged ? 'text-signal-deep' : 'text-ink-muted'
        )}
      >
        {label}
      </div>
      <div className="mt-2 font-numeric text-[26px] font-semibold leading-none tabular-nums">
        {value}
      </div>
      {delta && (
        <div
          className={cn(
            'mt-1 text-[10px]',
            flagged
              ? 'text-signal-deep'
              : tone === 'up'
                ? 'text-[color:var(--sss-success)]'
                : tone === 'down'
                  ? 'text-destructive'
                  : 'text-ink-muted'
          )}
        >
          {delta}
        </div>
      )}
    </div>
  )
}

export {
  Card,
  CardHeader,
  CardTitle,
  CardLabel,
  CardDescription,
  CardContent,
  CardFooter,
  StatTile,
}
